import { useState } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Button,
  Textarea,
} from '@duck/ui'
import { apis } from '@/modules/apiClients'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useCohort } from '@/hooks/useCohorts'

interface AddCohortPersonDialogProps {
  isOpen: boolean
  onClose: () => void
  cohortId: string
  onPersonAdded?: () => void
}

export const AddCohortPersonDialog = ({
  isOpen,
  onClose,
  cohortId,
  onPersonAdded,
}: AddCohortPersonDialogProps) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { cohort, refetch: refetchCohort } = useCohort(cohortId)
  const [activeTab, setActiveTab] = useState<'single' | 'multiple'>('single')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Multiple people CSV state
  const [csvInput, setCsvInput] = useState('')
  const [parsedRows, setParsedRows] = useState<
    { firstName: string; lastName: string; email: string; isEmailValid: boolean }[]
  >([])

  // Single person form state
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('')
      setFirstName('')
      setLastName('')
      setError(null)
      setCsvInput('')
      setParsedRows([])
      setActiveTab('single')
      onClose()
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setError(null)

    if (!cohort) {
      setError('Not found. Please refresh the page and try again.')
      return
    }

    try {
      setIsSubmitting(true)

      if (activeTab === 'single') {
        // Validate single person form
        if (!email || !firstName || !lastName) {
          setError('Email, first name, and last name are required.')
          return
        }

        const createPersonRequest: UserService.CreatePersonRequest = {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          organizationId: cohort.organizationId,
        }

        await apis.partnerService.cohortsAddPeoplePost({
          cohortId,
          people: [createPersonRequest],
        })
      } else {
        // Multiple tab
        if (parsedRows.length === 0) {
          setError('Please add at least one person.')
          return
        }

        if (parsedRows.some((row) => !row.isEmailValid)) {
          setError('Please fix invalid email addresses before submitting.')
          return
        }

        const people: UserService.CreatePersonRequest[] = parsedRows.map((row) => ({
          email: row.email.trim(),
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          organizationId: cohort.organizationId,
        }))

        await apis.partnerService.cohortsAddPeoplePost({
          cohortId,
          people,
        })
      }

      // Refetch cohort to update the list
      await refetchCohort()

      // Call callback to refresh the parent component
      onPersonAdded?.()

      // Close dialog and reset form
      handleClose()
    } catch (err: unknown) {
      console.error('Failed to add person to cohort:', err)
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to add person to cohort. Please try again.'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCsvChange = (value: string) => {
    setCsvInput(value)

    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    const rows: {
      firstName: string
      lastName: string
      email: string
      isEmailValid: boolean
    }[] = []

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    for (const line of lines) {
      const [firstName = '', lastName = '', email = ''] = line.split(',').map((part) => part.trim())

      if (!firstName && !lastName && !email) {
        continue
      }

      const isEmailValid = email.length === 0 ? false : emailRegex.test(email)

      rows.push({ firstName, lastName, email, isEmailValid })
    }

    setParsedRows(rows)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSubmitting && !open && handleClose()}
    >
      <DialogContent
        className="sm:max-w-md sm:min-w-[400px]"
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onInteractOutside={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {tP('addPerson')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <Button
            type="button"
            size="xs"
            variant={activeTab === 'single' ? 'default' : 'outline'}
            onClick={() => setActiveTab('single')}
            disabled={isSubmitting}
          >
            {t('common.single')}
          </Button>
          <Button
            type="button"
            size="xs"
            variant={activeTab === 'multiple' ? 'default' : 'outline'}
            onClick={() => setActiveTab('multiple')}
            disabled={isSubmitting}
          >
            {t('common.multiple')}
          </Button>
        </div>

        {activeTab === 'single' && (
          <div className="space-y-4 mt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="person-email" className="block text-sm font-medium text-foreground">
                  {t('common.email')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-email"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.email').toLowerCase() })}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="person-firstName" className="block text-sm font-medium text-foreground">
                  {t('common.firstName')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-firstName"
                  type="text"
                  autoComplete="off"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.firstName').toLowerCase() })}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="person-lastName" className="block text-sm font-medium text-foreground">
                  {t('common.lastName')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-lastName"
                  type="text"
                  autoComplete="off"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.lastName').toLowerCase() })}
                />
              </div>

              {error && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 me-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('common.saving')}
                    </>
                  ) : (
                    t('common.add')
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'multiple' && (
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="csv-input"
                className="block text-sm font-medium text-foreground"
              >
                CSV ({'firstName,lastName,email'})
              </label>
              <Textarea
                id="csv-input"
                autoComplete="off"
                value={csvInput}
                onChange={(e) => handleCsvChange(e.target.value)}
                disabled={isSubmitting}
                placeholder="firstName,lastName,email&#10;Jane,Doe,jane@example.com&#10;John,Smith,john@company.com"
                className="min-h-[140px] resize-y font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Each line should contain{' '}
                <span className="font-semibold">firstName,lastName,email</span>{' '}
                separated by commas.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {parsedRows.length > 0 && (
              <div className="space-y-3">
                {(() => {
                  const failedCount = parsedRows.filter((row) => !row.isEmailValid).length

                  return (
                    <p className="text-xs text-muted-foreground">
                      Preview ({parsedRows.length} row{parsedRows.length === 1 ? '' : 's'}
                      {failedCount > 0 && (
                        <>
                          ,{' '}
                          <span className="text-warning font-medium">
                            {failedCount} invalid
                          </span>
                        </>
                      )}
                      ):
                    </p>
                  )
                })()}

                <div className="max-h-48 overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.firstName')}
                        </th>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.lastName')}
                        </th>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.email')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, index) => (
                        <tr
                          key={`${row.email || index}-${index}`}
                          className={index % 2 === 0 ? 'bg-background' : 'bg-muted/40'}
                        >
                          <td className="px-3 py-1.5 align-top break-words">
                            {row.firstName}
                          </td>
                          <td className="px-3 py-1.5 align-top break-words">
                            {row.lastName}
                          </td>
                          <td
                            className={`px-3 py-1.5 align-top break-words ${row.isEmailValid ? '' : 'border border-error'
                              }`}
                          >
                            {row.email}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      isSubmitting ||
                      parsedRows.length === 0 ||
                      parsedRows.some((row) => !row.isEmailValid)
                    }
                    onClick={() => handleSubmit()}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 me-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('common.saving')}
                      </>
                    ) : (
                      t('common.add')
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
